import { validarCPF, validarCNPJ } from './utils.ts';
import type { StatusSolicitacaoCancelamento } from './cancelamentos';
import { resumoAnaliseFinanceira } from './analiseFinanceiraCancelamento.ts';
import type { AnaliseFinanceira } from './analiseFinanceiraCancelamento.ts';

// `validarCancelamento` é lógica pura que precisa de teste; por isso o import de
// `utils.ts` vem COM extensão (o Node ESM não resolve sem ela) e `supabase` só
// entra por import dinâmico dentro das funções de I/O, no fim do arquivo.
// Mesma restrição documentada em src/lib/cancelamentos.ts.

export type SeveridadeValidacao = 'ok' | 'atencao' | 'bloqueio';

export type ChaveValidacao =
  | 'cliente_vinculado'
  | 'dados_cadastrais'
  | 'telefone'
  | 'contrato_ativo'
  | 'solicitacao_duplicada'
  | 'cancelamento_anterior'
  | 'pendencias';

/** Campos do cadastro da UC que a tela de validação deixa corrigir na hora. */
export type CampoUcEditavel =
  | 'nome_cliente' | 'cpf_cnpj_cliente' | 'email_cliente' | 'whatsapp_cliente'
  | 'cep' | 'logradouro' | 'numero' | 'bairro' | 'cidade' | 'estado';

export interface ItemValidacao {
  chave: ChaveValidacao;
  titulo: string;
  severidade: SeveridadeValidacao;
  detalhe: string;
  /** Preenchido quando o item se resolve editando o cadastro na própria tela. */
  camposParaCorrigir?: CampoUcEditavel[];
}

export interface UcParaValidacao {
  id: number;
  numero_uc: string | null;
  nome_cliente: string | null;
  situacao: string | null;
  cpf_cnpj_cliente: string | null;
  cpf_cnpj_titular: string | null;
  titular_fatura: string | null;
  email_cliente: string | null;
  whatsapp_cliente: string | null;
  cep: string | null;
  logradouro: string | null;
  numero: string | null;
  bairro: string | null;
  cidade: string | null;
  estado: string | null;
  negociacao_id: number | null;
  rateio_motivo_travamento: string | null;
}

export interface SolicitacaoIrma {
  id: number;
  status: StatusSolicitacaoCancelamento;
  data_solicitacao: string;
  termo_cancelamento_log_id: number | null;
}

export interface ContextoValidacao {
  uc: UcParaValidacao;
  /** A solicitação sendo validada — sai da checagem de duplicidade. */
  solicitacaoAtualId: number | null;
  /** Todas as solicitações da MESMA UC, inclusive a atual. */
  solicitacoesDaUc: SolicitacaoIrma[];
  /** Negociação de origem: `dataAssinaturaTermo` preenchida = contrato ativo. */
  contrato: { negociacaoId: number; dataAssinaturaTermo: string | null; fidelidade: string | null } | null;
  faturasEmAbertoQtd: number;
  faturasEmAbertoValor: number;
  saldoAcumuladoKwh: number | null;
  consumoMedioKwh: number | null;
  /** Mês de referência da fatura de onde saiu o saldo (#5829): o número pode ser antigo. */
  saldoMesReferencia: string | null;
  /** Valores dos boletos, do mais recente para o mais antigo — base da média mensal (#5829). */
  valoresBoletos: Array<number | null>;
  /** Só as contribuições PAGAS, mais recente primeiro — base da multa (cláusula 13.4-I). */
  valoresBoletosPagos: Array<number | null>;
  /** Vencimento da primeira fatura com compensação — marco da fidelidade e da carência (13.7/13.8). */
  dataPrimeiraCompensacao: string | null;
  /** Só os boletos em aberto COM valor: é o que dá para cobrar do cliente, e a
      mesma regra do cron `popular_alerta_faturas_em_aberto` (#5829). O checklist
      acima segue contando todos, inclusive os de R$ 0,00. */
  faturasCobraveisQtd: number;
  faturasCobraveisValor: number;
  /** Número do distrato já ativo nesta solicitação, se houver. */
  distratoAtivoNumero: string | null;
}

export interface ResultadoValidacao {
  itens: ItemValidacao[];
  bloqueios: ItemValidacao[];
  atencoes: ItemValidacao[];
  /** Sem nenhum bloqueio: pode seguir para o distrato sem justificar nada. */
  liberado: boolean;
  /** União dos campos que a tela precisa abrir para edição. */
  camposParaCorrigir: CampoUcEditavel[];
}

const vazio = (v: string | null | undefined) => !String(v ?? '').trim();

/** Situações em que a UC ainda participa do rateio — ou seja, há o que cancelar. */
const SITUACOES_ATIVAS = ['Ativa', 'Ativa-S/-Rateio', '1° Ciclo', '2° Ciclo', 'Reativacao'];

const soDigitos = (v: string | null | undefined) => String(v ?? '').replace(/\D/g, '');

const fmtBRL = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const fmtData = (v: string | null | undefined): string => {
  if (!v) return '—';
  const [ano, mes, dia] = String(v).slice(0, 10).split('-');
  return dia && mes && ano ? `${dia}/${mes}/${ano}` : String(v);
};

/**
 * Telefone brasileiro utilizável para mandar o link de assinatura: 10 dígitos
 * (fixo com DDD) ou 11 (celular com DDD). Aceita o 55 na frente — é como o
 * WhatsApp devolve — mas exige que sobre um número com DDD depois de tirar.
 */
export function telefoneUtilizavel(valor: string | null | undefined): boolean {
  let d = soDigitos(valor);
  if (d.length > 11 && d.startsWith('55')) d = d.slice(2);
  if (d.length !== 10 && d.length !== 11) return false;
  const ddd = Number(d.slice(0, 2));
  if (ddd < 11 || ddd > 99) return false;
  // Celular de 11 dígitos sempre começa com 9 depois do DDD.
  if (d.length === 11 && d[2] !== '9') return false;
  // Repetição total ("11999999999" é placeholder de cadastro, não telefone).
  return !/^(\d)\1+$/.test(d.slice(2));
}

/** CPF (11) ou CNPJ (14) com dígito verificador correto. */
export function documentoValido(valor: string | null | undefined): boolean {
  const d = soDigitos(valor);
  if (d.length === 11) return validarCPF(d);
  if (d.length === 14) return validarCNPJ(d);
  return false;
}

/**
 * Checklist da etapa "Validar cancelamento" — a última barreira antes de o
 * distrato ir para assinatura. Pura de propósito: recebe o contexto já buscado
 * (`carregarContextoValidacao`) e devolve o quadro que a tela desenha.
 *
 * Severidade:
 *   • `bloqueio` — segurar aqui evita um documento errado ou duplicado saindo
 *     para o cliente. A tela só libera depois de corrigir OU justificar por
 *     escrito (a justificativa fica gravada com quem validou).
 *   • `atencao`  — vale o atendente saber, mas não impede o distrato: são casos
 *     legítimos (inadimplência é o motivo mais comum de cancelamento, então
 *     fatura em aberto não pode barrar nada).
 *
 * A ordem da lista é a ordem em que aparece na tela: primeiro quem é o cliente,
 * depois o contato, depois o contrato, depois duplicidade, e por último o
 * panorama financeiro.
 */
export function validarCancelamento(ctx: ContextoValidacao): ResultadoValidacao {
  const { uc } = ctx;
  const itens: ItemValidacao[] = [];

  // -------------------------------------------------------------------------
  // 1. O cliente está mesmo vinculado a esta UC?
  // -------------------------------------------------------------------------
  const docCliente = soDigitos(uc.cpf_cnpj_cliente);
  const docTitular = soDigitos(uc.cpf_cnpj_titular);

  if (vazio(uc.nome_cliente)) {
    itens.push({
      chave: 'cliente_vinculado', titulo: 'Cliente vinculado à UC', severidade: 'bloqueio',
      detalhe: 'A UC está sem nome de cliente no cadastro. O distrato sairia sem contratante identificado.',
      camposParaCorrigir: ['nome_cliente', 'cpf_cnpj_cliente'],
    });
  } else if (!docCliente && docTitular) {
    // O CancelUCModal usa `cpf_cnpj_cliente || cpf_cnpj_titular`: sem o do
    // cliente, o termo sai com o NOME do cliente e o CPF do titular da fatura —
    // duas pessoas diferentes no mesmo documento.
    itens.push({
      chave: 'cliente_vinculado', titulo: 'Cliente vinculado à UC', severidade: 'bloqueio',
      detalhe: `A UC não tem CPF/CNPJ do cliente, só o do titular da fatura${uc.titular_fatura ? ` (${uc.titular_fatura})` : ''}. `
        + 'Do jeito que está, o distrato sairia com o nome do cliente e o documento do titular — duas pessoas diferentes no mesmo termo.',
      camposParaCorrigir: ['cpf_cnpj_cliente'],
    });
  } else if (!docCliente) {
    itens.push({
      chave: 'cliente_vinculado', titulo: 'Cliente vinculado à UC', severidade: 'bloqueio',
      detalhe: 'A UC está sem CPF/CNPJ do cliente. O distrato precisa do documento de quem assina.',
      camposParaCorrigir: ['cpf_cnpj_cliente'],
    });
  } else if (!documentoValido(docCliente)) {
    itens.push({
      chave: 'cliente_vinculado', titulo: 'Cliente vinculado à UC', severidade: 'bloqueio',
      detalhe: `O CPF/CNPJ do cliente (${uc.cpf_cnpj_cliente}) não passa na conferência do dígito verificador — provavelmente está digitado errado.`,
      camposParaCorrigir: ['cpf_cnpj_cliente'],
    });
  } else if (docTitular && docTitular !== docCliente) {
    itens.push({
      chave: 'cliente_vinculado', titulo: 'Cliente vinculado à UC', severidade: 'atencao',
      detalhe: `A UC está no nome de outra pessoa na distribuidora${uc.titular_fatura ? ` (titular: ${uc.titular_fatura})` : ''}. `
        + 'O distrato sai no nome do cliente contratante, que é o correto — só confira se é essa mesmo a pessoa que pediu o cancelamento.',
    });
  } else {
    itens.push({
      chave: 'cliente_vinculado', titulo: 'Cliente vinculado à UC', severidade: 'ok',
      detalhe: `${uc.nome_cliente} — CPF/CNPJ confere com o cadastro da UC ${uc.numero_uc || ''}`.trim(),
    });
  }

  // -------------------------------------------------------------------------
  // 2. Dados cadastrais completos (e-mail é obrigatório no distrato; endereço
  //    vai impresso no termo, mas não impede a emissão)
  // -------------------------------------------------------------------------
  const faltamEndereco = ([
    ['cep', uc.cep, 'CEP'], ['logradouro', uc.logradouro, 'rua'], ['numero', uc.numero, 'número'],
    ['bairro', uc.bairro, 'bairro'], ['cidade', uc.cidade, 'cidade'], ['estado', uc.estado, 'estado'],
  ] as Array<[CampoUcEditavel, string | null, string]>).filter(([, v]) => vazio(v));

  if (vazio(uc.email_cliente)) {
    itens.push({
      chave: 'dados_cadastrais', titulo: 'Dados cadastrais completos', severidade: 'bloqueio',
      detalhe: 'Sem e-mail do cliente. O Autentique manda o link de assinatura por e-mail — sem ele o distrato não chega.',
      camposParaCorrigir: ['email_cliente', ...faltamEndereco.map(([campo]) => campo)],
    });
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(uc.email_cliente).trim())) {
    itens.push({
      chave: 'dados_cadastrais', titulo: 'Dados cadastrais completos', severidade: 'bloqueio',
      detalhe: `O e-mail "${uc.email_cliente}" não tem formato válido — o link de assinatura voltaria como não entregue.`,
      camposParaCorrigir: ['email_cliente', ...faltamEndereco.map(([campo]) => campo)],
    });
  } else if (faltamEndereco.length > 0) {
    itens.push({
      chave: 'dados_cadastrais', titulo: 'Dados cadastrais completos', severidade: 'atencao',
      detalhe: `Endereço do cliente incompleto no cadastro (falta ${faltamEndereco.map(([, , rotulo]) => rotulo).join(', ')}). `
        + 'O distrato é emitido assim mesmo, mas sai com o endereço pela metade.',
      camposParaCorrigir: faltamEndereco.map(([campo]) => campo),
    });
  } else {
    itens.push({
      chave: 'dados_cadastrais', titulo: 'Dados cadastrais completos', severidade: 'ok',
      detalhe: 'E-mail e endereço completos no cadastro da UC.',
    });
  }

  // -------------------------------------------------------------------------
  // 3. Telefone
  // -------------------------------------------------------------------------
  if (vazio(uc.whatsapp_cliente)) {
    itens.push({
      chave: 'telefone', titulo: 'Telefone de contato', severidade: 'bloqueio',
      detalhe: 'A UC está sem WhatsApp do cliente. É por ele que o link de assinatura do distrato é enviado.',
      camposParaCorrigir: ['whatsapp_cliente'],
    });
  } else if (!telefoneUtilizavel(uc.whatsapp_cliente)) {
    itens.push({
      chave: 'telefone', titulo: 'Telefone de contato', severidade: 'bloqueio',
      detalhe: `O número "${uc.whatsapp_cliente}" não é um telefone válido com DDD — confira antes de mandar o distrato.`,
      camposParaCorrigir: ['whatsapp_cliente'],
    });
  } else {
    itens.push({
      chave: 'telefone', titulo: 'Telefone de contato', severidade: 'ok',
      detalhe: `WhatsApp ${uc.whatsapp_cliente} cadastrado.`,
    });
  }

  // -------------------------------------------------------------------------
  // 4. Contrato ativo (termo de adesão assinado na negociação de origem)
  // -------------------------------------------------------------------------
  if (!ctx.contrato) {
    itens.push({
      chave: 'contrato_ativo', titulo: 'Contrato ativo', severidade: 'atencao',
      detalhe: uc.negociacao_id
        ? 'A negociação de origem desta UC não foi encontrada — não dá para conferir o contrato pelo sistema.'
        : 'A UC não tem negociação vinculada (cadastro antigo ou importado). Confira o contrato fora do sistema antes de emitir o distrato.',
    });
  } else if (!ctx.contrato.dataAssinaturaTermo) {
    itens.push({
      chave: 'contrato_ativo', titulo: 'Contrato ativo', severidade: 'atencao',
      detalhe: 'O termo de adesão desta UC não consta como assinado. Sem contrato assinado não existe o que distratar — '
        + 'confira se o caso é de cancelamento de contrato ou só de desistência antes da adesão.',
    });
  } else {
    itens.push({
      chave: 'contrato_ativo', titulo: 'Contrato ativo', severidade: 'ok',
      detalhe: `Termo de adesão assinado em ${fmtData(ctx.contrato.dataAssinaturaTermo)}.`,
    });
  }

  // -------------------------------------------------------------------------
  // 5. Já existe outra solicitação em andamento para a mesma UC?
  // -------------------------------------------------------------------------
  const emAndamento = ctx.solicitacoesDaUc.filter(s =>
    s.id !== ctx.solicitacaoAtualId && (s.status === 'aberta' || s.status === 'em_andamento'));

  if (emAndamento.length > 0) {
    itens.push({
      chave: 'solicitacao_duplicada', titulo: 'Solicitação duplicada', severidade: 'bloqueio',
      detalhe: `Esta UC já tem ${emAndamento.length === 1 ? 'outra solicitação' : `outras ${emAndamento.length} solicitações`} `
        + `de cancelamento em andamento (${emAndamento.map(s => `#${s.id} de ${fmtData(s.data_solicitacao)}`).join(', ')}). `
        + 'Seguir daqui gera um segundo distrato para o mesmo cliente.',
    });
  } else {
    itens.push({
      chave: 'solicitacao_duplicada', titulo: 'Solicitação duplicada', severidade: 'ok',
      detalhe: 'Nenhuma outra solicitação de cancelamento aberta para esta UC.',
    });
  }

  // -------------------------------------------------------------------------
  // 6. Já houve cancelamento concluído / a UC já está cancelada?
  // -------------------------------------------------------------------------
  const concluidas = ctx.solicitacoesDaUc.filter(s => s.id !== ctx.solicitacaoAtualId && s.status === 'concluida');
  const ucJaCancelada = uc.situacao === 'Cancelada';

  if (ucJaCancelada || concluidas.length > 0) {
    const partes: string[] = [];
    if (ucJaCancelada) partes.push('a UC já está com situação "Cancelada" no cadastro');
    if (concluidas.length > 0) {
      partes.push(`já existe cancelamento concluído (${concluidas.map(s => `#${s.id} de ${fmtData(s.data_solicitacao)}`).join(', ')})`);
    }
    itens.push({
      chave: 'cancelamento_anterior', titulo: 'Cancelamento anterior', severidade: 'bloqueio',
      detalhe: `Atenção: ${partes.join(' e ')}. Confirme que este pedido é de outra UC ou de uma reativação antes de emitir um novo distrato.`,
    });
  } else if (uc.situacao && !SITUACOES_ATIVAS.includes(uc.situacao)) {
    itens.push({
      chave: 'cancelamento_anterior', titulo: 'Cancelamento anterior', severidade: 'atencao',
      detalhe: `A UC está com situação "${uc.situacao}" — não está participando do rateio hoje. Confira se o cancelamento ainda faz sentido.`,
    });
  } else {
    itens.push({
      chave: 'cancelamento_anterior', titulo: 'Cancelamento anterior', severidade: 'ok',
      detalhe: 'Nenhum cancelamento anterior registrado para esta UC.',
    });
  }

  // -------------------------------------------------------------------------
  // 7. Pendências e situações especiais — nunca bloqueiam (inadimplência é o
  //    motivo mais comum de cancelamento), mas o atendente precisa enxergar.
  // -------------------------------------------------------------------------
  const pendencias: string[] = [];
  if (ctx.faturasEmAbertoQtd > 0) {
    pendencias.push(`${ctx.faturasEmAbertoQtd} fatura(s) em aberto somando ${fmtBRL(ctx.faturasEmAbertoValor)}`);
  }
  if (ctx.saldoAcumuladoKwh !== null && ctx.saldoAcumuladoKwh > 0) {
    const cobertura = ctx.consumoMedioKwh && ctx.consumoMedioKwh > 0
      ? ` (~${(ctx.saldoAcumuladoKwh / ctx.consumoMedioKwh).toFixed(1)} meses de consumo)` : '';
    pendencias.push(`saldo de ${Math.round(ctx.saldoAcumuladoKwh).toLocaleString('pt-BR')} kWh a usar${cobertura}`);
  }
  if (uc.rateio_motivo_travamento) {
    pendencias.push(uc.rateio_motivo_travamento === 'excesso_saldo'
      ? 'UC pausada automaticamente por excesso de saldo'
      : 'UC com o rateio travado manualmente');
  }
  if (ctx.distratoAtivoNumero) {
    pendencias.push(`já existe distrato ativo nesta solicitação (${ctx.distratoAtivoNumero})`);
  }

  itens.push(pendencias.length > 0
    ? {
      chave: 'pendencias', titulo: 'Pendências e situações especiais', severidade: 'atencao',
      detalhe: `${pendencias.join('; ')}. Resolva ou registre nas observações antes de fechar o distrato.`,
    }
    : {
      chave: 'pendencias', titulo: 'Pendências e situações especiais', severidade: 'ok',
      detalhe: 'Sem faturas em aberto, saldo remanescente ou travamento de rateio.',
    });

  const bloqueios = itens.filter(i => i.severidade === 'bloqueio');
  const atencoes = itens.filter(i => i.severidade === 'atencao');
  const camposParaCorrigir = Array.from(new Set(itens.flatMap(i => i.camposParaCorrigir || [])));

  return { itens, bloqueios, atencoes, liberado: bloqueios.length === 0, camposParaCorrigir };
}

// ===========================================================================
// I/O — monta o contexto a partir do banco. Import dinâmico do supabase para
// não quebrar o `node --test` do bloco puro acima.
// ===========================================================================

const CAMPOS_UC_VALIDACAO =
  'id, numero_uc, nome_cliente, situacao, cpf_cnpj_cliente, cpf_cnpj_titular, titular_fatura, ' +
  'email_cliente, whatsapp_cliente, cep, logradouro, numero, bairro, cidade, estado, ' +
  'negociacao_id, rateio_motivo_travamento, consumo_medio_kwh, outro_numero_uc';

export async function carregarContextoValidacao(
  ucId: number,
  solicitacaoAtualId: number | null,
): Promise<ContextoValidacao> {
  const { supabase } = await import('./supabase');
  const { ucCodigoKeys } = await import('./ucCodigo');
  const { agregarFaturasDaUc } = await import('./cancelamentos');

  const { data: ucRow, error: ucErro } = await supabase
    .from('unidades_consumidoras').select(CAMPOS_UC_VALIDACAO).eq('id', ucId).single();
  if (ucErro) throw ucErro;
  const ucData = ucRow as any;

  const [solicitacoes, contrato, faturas, distrato] = await Promise.all([
    (async () => {
      const { data, error } = await supabase
        .from('solicitacoes_cancelamento')
        .select('id, status, data_solicitacao, termo_cancelamento_log_id')
        .eq('uc_id', ucId);
      if (error) throw error;
      return (data || []) as SolicitacaoIrma[];
    })(),
    (async () => {
      if (!ucData.negociacao_id) return null;
      const { data, error } = await supabase
        .from('negociacoes').select('id, data_assinatura_termo, fidelidade').eq('id', ucData.negociacao_id).maybeSingle();
      if (error) throw error;
      return data
        ? { negociacaoId: data.id, dataAssinaturaTermo: data.data_assinatura_termo ?? null, fidelidade: data.fidelidade ?? null }
        : null;
    })(),
    (async () => {
      const chaves = ucCodigoKeys(ucData.numero_uc, ucData.outro_numero_uc);
      if (chaves.length === 0) {
        return { saldoAcumuladoKwh: null, qtdAberto: 0, valorAberto: 0, mesReferencia: null, valores: [],
                 cobraveisQtd: 0, cobraveisValor: 0, pagos: [], primeiraCompensacao: null };
      }
      // Uma UC só: cabe folgado no teto de 1.000 linhas do PostgREST, não
      // precisa da paginação que `enriquecerComFaturas` faz para a lista toda.
      const { data, error } = await supabase
        .from('faturas')
        .select('saldo_ugs_vigor, status_boleto_vigor, valor_boleto_vigor, valor_emissao_boleto_unico, status_fatura, mes_referencia, economia, data_vencimento')
        .in('uc_codigo_norm', chaves)
        .order('created_at', { ascending: false })
        .order('id', { ascending: false })
        .limit(500);
      if (error) throw error;
      const linhas = (data || []) as any[];
      // Mesma regra do agregado: fatura excluída não conta para nada (#5829).
      const validas = linhas.filter(f => (f.status_fatura || '') !== 'Excluído');
      const cobraveis = validas.filter(f => ['Em aberto', 'Vencido'].includes(f.status_boleto_vigor) && Number(f.valor_boleto_vigor || 0) > 0);
      // Cláusula 13.4-I: só contribuição PAGA forma a base da multa.
      const pagas = validas.filter(f => ['Pago', 'Quitado'].includes(f.status_boleto_vigor));
      // Cláusulas 13.7 e 13.8: o marco é a primeira fatura com compensação de
      // créditos. `data_vencimento` é data de verdade da distribuidora;
      // `mes_referencia` é texto ("AGO/2026") e ordena errado.
      const comCompensacao = validas
        .filter(f => Number(f.economia || 0) > 0 && f.data_vencimento)
        .map(f => String(f.data_vencimento).slice(0, 10))
        .sort();
      return {
        ...agregarFaturasDaUc(linhas as any),
        pagos: pagas.map(f => (f.valor_boleto_vigor === null || f.valor_boleto_vigor === undefined ? null : Number(f.valor_boleto_vigor))),
        primeiraCompensacao: comCompensacao[0] ?? null,
        cobraveisQtd: cobraveis.length,
        cobraveisValor: cobraveis.reduce((soma, f) => soma + Number(f.valor_boleto_vigor || 0), 0),
        mesReferencia: (validas[0]?.mes_referencia as string | null) ?? null,
        valores: validas.map(f => (f.valor_boleto_vigor === null || f.valor_boleto_vigor === undefined ? null : Number(f.valor_boleto_vigor))),
      };
    })(),
    (async () => {
      if (!solicitacaoAtualId) return null;
      const { data, error } = await supabase
        .from('solicitacoes_cancelamento')
        .select('termo_cancelamento_logs ( numero_termo, cancelado_em )')
        .eq('id', solicitacaoAtualId).maybeSingle();
      if (error) throw error;
      const termo = (data as any)?.termo_cancelamento_logs;
      return termo && !termo.cancelado_em ? (termo.numero_termo as string | null) || 'sem número' : null;
    })(),
  ]);

  return {
    uc: {
      id: ucData.id,
      numero_uc: ucData.numero_uc ?? null,
      nome_cliente: ucData.nome_cliente ?? null,
      situacao: ucData.situacao ?? null,
      cpf_cnpj_cliente: ucData.cpf_cnpj_cliente ?? null,
      cpf_cnpj_titular: ucData.cpf_cnpj_titular ?? null,
      titular_fatura: ucData.titular_fatura ?? null,
      email_cliente: ucData.email_cliente ?? null,
      whatsapp_cliente: ucData.whatsapp_cliente ?? null,
      cep: ucData.cep ?? null,
      logradouro: ucData.logradouro ?? null,
      numero: ucData.numero ?? null,
      bairro: ucData.bairro ?? null,
      cidade: ucData.cidade ?? null,
      estado: ucData.estado ?? null,
      negociacao_id: ucData.negociacao_id ?? null,
      rateio_motivo_travamento: ucData.rateio_motivo_travamento ?? null,
    },
    solicitacaoAtualId,
    solicitacoesDaUc: solicitacoes,
    contrato,
    faturasEmAbertoQtd: faturas.qtdAberto,
    faturasEmAbertoValor: faturas.valorAberto,
    saldoAcumuladoKwh: faturas.saldoAcumuladoKwh,
    consumoMedioKwh: ucData.consumo_medio_kwh ?? null,
    valoresBoletosPagos: faturas.pagos ?? [],
    dataPrimeiraCompensacao: faturas.primeiraCompensacao ?? null,
    faturasCobraveisQtd: faturas.cobraveisQtd ?? 0,
    faturasCobraveisValor: faturas.cobraveisValor ?? 0,
    saldoMesReferencia: faturas.mesReferencia ?? null,
    valoresBoletos: faturas.valores ?? [],
    distratoAtivoNumero: distrato,
  };
}

/** Grava no cadastro da UC as correções feitas na tela de validação. */
export async function corrigirCadastroUc(
  ucId: number,
  campos: Partial<Record<CampoUcEditavel, string>>,
): Promise<void> {
  const patch: Record<string, string | null> = {};
  (Object.entries(campos) as Array<[CampoUcEditavel, string]>).forEach(([campo, valor]) => {
    patch[campo] = String(valor ?? '').trim() || null;
  });
  if (Object.keys(patch).length === 0) return;
  const { supabase } = await import('./supabase');
  const { error } = await supabase.from('unidades_consumidoras').update(patch).eq('id', ucId);
  if (error) throw error;
}

/**
 * Carimba a etapa "Validar cancelamento" (RPC SECURITY DEFINER — ver migration
 * 20260909120000). O snapshot é gravado como foi VISTO na tela: recalcular
 * depois daria outro resultado, e o que a auditoria precisa saber é o que o
 * atendente tinha na frente quando decidiu seguir.
 */
export async function registrarValidacao(
  solicitacaoId: number,
  itens: ItemValidacao[],
  justificativa: string,
  /** Análise financeira vista na tela (#5829) — vai no mesmo snapshot, com severidade 'ok'
      porque informa e não bloqueia. A tarefa 07 lê daqui para montar a mensagem. */
  analise?: AnaliseFinanceira | null,
): Promise<void> {
  const { supabase } = await import('./supabase');
  const financeiro = analise
    ? [{ chave: 'analise_financeira', titulo: 'Análise financeira', severidade: 'ok', detalhe: resumoAnaliseFinanceira(analise), dados: analise }]
    : [];
  const { error } = await supabase.rpc('registrar_validacao_cancelamento', {
    p_solicitacao_id: solicitacaoId,
    p_itens: [
      ...itens.map(i => ({ chave: i.chave, titulo: i.titulo, severidade: i.severidade, detalhe: i.detalhe })),
      ...financeiro,
    ],
    p_justificativa: justificativa.trim() || null,
  });
  if (error) throw error;
}
